import {Property} from '@mintdata/mint-kit/api/decorators/Property';
import {Units} from '@mintdata/mint-kit/api/models/Units';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {UnitsConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/UnitsConfig';

export class Offsets
{
    @Property<Units>({
        title: '',
        tip: '',
        isRequired: true,
        typeConfig: UnitsConfig,
        typeCtor: Units
    })
    public top:Units = Units.fromPixels(0);

    @Property<Units>({
        title: '',
        tip: '',
        isRequired: true,
        typeConfig: UnitsConfig,
        typeCtor: Units
    })
    public right:Units = Units.fromPixels(0);

    @Property<Units>({
        title: '',
        tip: '',
        isRequired: true,
        typeConfig: UnitsConfig,
        typeCtor: Units
    })
    public bottom:Units = Units.fromPixels(0);

    @Property<Units>({
        title: '',
        tip: '',
        isRequired: true,
        typeConfig: UnitsConfig,
        typeCtor: Units
    })
    public left:Units = Units.fromPixels(0);

    public constructor(
        _top,
        _right,
        _bottom,
        _left
    )
    {
        this.top = _top;
        this.right = _right;
        this.bottom = _bottom;
        this.left = _left;
    }

    @Property<Units>({
        title: '',
        tip: '',
        isRequired: true,
        typeConfig: UnitsConfig,
        typeCtor: Units
    })
    public get value():Units
    {
        return this._areValuesEqual()
            ? this.top
            : null;
    }

    public set value(value:Units)
    {
        this.top = value;
        this.right = value;
        this.bottom = value;
        this.left = value;
    }

    public get leftUnitsString():AString
    {
        return this.left && this.left.unitsString;
    }

    public set leftUnitsString(leftUnitsString:AString)
    {
        if( !leftUnitsString || leftUnitsString.value == null ) return;

        this.left = Units.fromUnitsString(leftUnitsString);
    }

    public get topUnitsString():AString
    {
        return this.top && this.top.unitsString;
    }

    public set topUnitsString(topUnitsString:AString)
    {
        if( !topUnitsString || topUnitsString.value == null ) return;

        this.top = Units.fromUnitsString(topUnitsString);
    }

    public get rightUnitsString():AString
    {
        return this.right && this.right.unitsString;
    }

    public set rightUnitsString(rightUnitsString:AString)
    {
        if( !rightUnitsString || rightUnitsString.value == null ) return;

        this.right = Units.fromUnitsString(rightUnitsString);
    }

    public get bottomUnitsString():AString
    {
        return this.bottom && this.bottom.unitsString;
    }

    public set bottomUnitsString(bottomUnitsString:AString)
    {
        if( !bottomUnitsString || bottomUnitsString.value == null ) return;

        this.bottom = Units.fromUnitsString(bottomUnitsString);
    }

    public toString():string
    {
        return this.top && this.left && this.right && this.bottom
            ? `${this.top} ${this.right} ${this.bottom} ${this.left}`
            : null;
    }

    public toCSS():string
    {
        return this.toString();
    }

    private _areValuesEqual():boolean
    {
        return (!this.top
                && !this.right
                && !this.bottom
                && !this.left
            )
            || (this.top.isEqualTo(this.right)
                && this.top.isEqualTo(this.bottom)
                && this.top.isEqualTo(this.left));
    }

    public isEqualTo(other:Offsets):boolean
    {
        return other
            && other.top
            && other.top.isEqualTo(this.top)
            && other.right
            && other.right.isEqualTo(this.right)
            && other.bottom
            && other.bottom.isEqualTo(this.bottom)
            && other.left
            && other.left.isEqualTo(this.left);
    }

    public static fromPixels(
        top:number,
        right:number,
        bottom:number,
        left:number
    ):Offsets
    {
        return new Offsets(
            Units.fromPixels(top),
            Units.fromPixels(right),
            Units.fromPixels(bottom),
            Units.fromPixels(left),
        );
    }

    public static fromEqualPixels(
        value:number
    ):Offsets
    {
        return new Offsets(
            Units.fromPixels(value),
            Units.fromPixels(value),
            Units.fromPixels(value),
            Units.fromPixels(value),
        );
    }

    public static fromPercentages(
        value:number,
        top:number,
        right:number,
        bottom:number,
        left:number
    ):Offsets
    {
        return new Offsets(
            Units.fromPercentages(top),
            Units.fromPercentages(right),
            Units.fromPercentages(bottom),
            Units.fromPercentages(left),
        );
    }

    public static fromEqualAuto():Offsets
    {
        return new Offsets(
            Units.auto(),
            Units.auto(),
            Units.auto(),
            Units.auto()
        );
    }
}
